import { LINK } from '@constants';
import {
  Box,
  Button,
  ChevronLineLeft,
  ChevronLineRight,
  Column,
  Divider,
  Icon,
  IconButton,
  Row,
  Spinner,
  Typography,
} from '@viewup/ui';
import { useGetCalender } from 'app/viewing-money/_features/api';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { buttonStyles, weeks } from './MonthlyCalendar.const';

export const MonthlyCalendar = () => {
  const calendarRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();
  const now = dayjs();
  const params = new URLSearchParams();
  const [currentYear, setCurrentYear] = useState(now.year());
  const [currentMonth, setCurrentMonth] = useState(now.month() + 1);
  const [dayArray, setDayArray] = useState<string[]>([]);
  const [selectValue, setSelectValue] = useState<string | undefined>(undefined);

  const { data, isPending } = useGetCalender({
    query: {
      monthValue: currentMonth,
      yearValue: currentYear,
    },
  });

  const handleClickTodayList = (today: string) => {
    const startDate = dayjs(`${currentYear}-${currentMonth}-${today}`).startOf('d').toISOString();
    const endDate = dayjs(`${currentYear}-${currentMonth}-${today}`).endOf('d').toISOString();

    params.set('startDate', startDate);
    params.set('endDate', endDate);

    router.push(`${LINK.VIEWING_MONEY_LIST}?${params}`);
  };

  const handleChangeMonths = (type: 'prev' | 'next') => {
    if (type === 'prev') {
      if (currentMonth === 1) {
        setCurrentYear((current) => current - 1);
        setCurrentMonth(12);
        return;
      }
      return setCurrentMonth((month) => month - 1);
    }
    if (type === 'next') {
      if (currentMonth === 12) {
        setCurrentYear((current) => current + 1);
        setCurrentMonth(1);
        return;
      }
      return setCurrentMonth((month) => month + 1);
    }
  };

  useEffect(() => {
    setSelectValue(undefined);

    const currentDaysInMonth = dayjs(`${currentYear}-${currentMonth}`).daysInMonth();
    const currentFirstDay = dayjs(`${currentYear}-${currentMonth}-01`).startOf('month').day();
    const newDateArray: string[] = [];

    for (let i = 0; i < currentFirstDay; i++) {
      newDateArray.push('');
    }

    for (let i = 1; i <= currentDaysInMonth; i++) {
      newDateArray.push(i.toString());
    }

    setDayArray(newDateArray);
  }, [currentMonth]);

  useEffect(() => {
    if (selectValue && calendarRef.current) {
      calendarRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectValue]);

  return (
    <Column bgColor='bg.primary' br={16} gap={16} p={16} shadow='center.normal'>
      <Column gap={8}>
        <Typography fontType='body.medium'>매달 적립 캘린더를 채워보세요!</Typography>
        <Typography color='text.assistance' fontType='body2.regular'>
          {(data?.data.length || 0) > 0
            ? '적립한 날짜를 클릭하면 포인트를 확인할 수 있어요.'
            : '뷰업활동을 통해 포인트를 모아보세요.'}
        </Typography>
      </Column>
      <Column>
        <Row alignItems='center' justifyContent='space-between' p={4}>
          <IconButton
            onClick={() => handleChangeMonths('prev')}
            size={'L'}
            variant='ghost.assistive'
          >
            <Icon color='icon.interactive.secondary' size='XS' source={ChevronLineLeft} />
          </IconButton>
          <Typography fontType='header3.medium'>
            {currentYear}.{currentMonth.toString().padStart(2, '0')}
          </Typography>
          <IconButton
            css={{
              cursor:
                currentYear === now.year() && currentMonth === now.month() + 1
                  ? 'default'
                  : 'pointer',
            }}
            onClick={() => {
              if (currentYear === now.year() && currentMonth === now.month() + 1) return;
              handleChangeMonths('next');
            }}
            size={'L'}
            variant='ghost.assistive'
          >
            <Icon
              color={
                currentYear === now.year() && currentMonth === now.month() + 1
                  ? 'icon.disabled'
                  : 'icon.interactive.secondary'
              }
              size='XS'
              source={ChevronLineRight}
            />
          </IconButton>
        </Row>
        <Box
          css={{
            gridTemplateColumns: 'repeat(7 , 1fr)',
            justifyItems: 'center',
          }}
          display='grid'
          gap={4}
          position='relative'
        >
          {weeks.map((week) => (
            <Row alignItems='center' height={40} justifyContent='center' key={week}>
              <Typography color='text.secondary' fontType='body2.regular'>
                {week}
              </Typography>
            </Row>
          ))}
          {dayArray.map((day, index) => (
            <Row
              alignItems='center'
              bgColor={
                selectValue === day
                  ? 'bg.info'
                  : data?.data
                        ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                        .find((item) => dayjs(item.createdAt).date() === Number(day))
                    ? 'bg.infoSubtle'
                    : 'bg.dimmer.0'
              }
              br={999}
              css={{ cursor: 'pointer' }}
              height={40}
              justifyContent='center'
              // biome-ignore lint/suspicious/noArrayIndexKey: <추후에 의미있는 키 값으로 변경>
              key={index}
              onClick={() => {
                if (
                  !data?.data
                    ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                    .find((item) => dayjs(item.createdAt).date() === Number(day))
                ) {
                  return;
                }
                if (selectValue === day) return setSelectValue(undefined);
                setSelectValue(day);
              }}
              width={40}
            >
              <Typography
                color={
                  selectValue === day
                    ? 'text.primaryInverse'
                    : data?.data
                          ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                          .find((item) => dayjs(item.createdAt).date() === Number(day))
                      ? 'text.info'
                      : 'text.tertiary'
                }
                fontType='body2.regular'
              >
                {day}
              </Typography>
            </Row>
          ))}

          {isPending && (
            <Row
              alignItems='center'
              height='100%'
              justifyContent='center'
              position='absolute'
              width='100%'
            >
              <Spinner variant={'round'} />
            </Row>
          )}
        </Box>

        {selectValue && (
          <Button
            onClick={() => handleClickTodayList(selectValue)}
            ref={calendarRef}
            size='L'
            styledComponents={{ ...buttonStyles }}
            variant='ghost.assistive'
          >
            <Column gap={12} width='100%'>
              {!!data?.data
                ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                .filter((item) => dayjs(item.createdAt).date() === Number(selectValue))[0]
                ?.earnedPoint && (
                <Row justifyContent='space-between' width='100%'>
                  <Typography color='text.tertiary' fontType='body2.regular'>
                    적립 포인트
                  </Typography>
                  <Typography color='text.info' fontType='body2.medium'>
                    {data?.data
                      ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                      .filter((item) => dayjs(item.createdAt).date() === Number(selectValue))[0]
                      .earnedPoint || 0}
                    P
                  </Typography>
                </Row>
              )}
              {!!data?.data
                ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                .filter((item) => dayjs(item.createdAt).date() === Number(selectValue))[0]
                ?.deductedPoint && (
                <Row justifyContent='space-between' width='100%'>
                  <Typography color='text.tertiary' fontType='body2.regular'>
                    차감 포인트
                  </Typography>
                  <Typography color='text.danger' fontType='body2.medium'>
                    {data?.data
                      ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                      .filter((item) => dayjs(item.createdAt).date() === Number(selectValue))[0]
                      .deductedPoint || 0}
                    P
                  </Typography>
                </Row>
              )}
              {!!data?.data
                ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                .filter((item) => dayjs(item.createdAt).date() === Number(selectValue))[0]
                ?.earnedPoint &&
                !!data?.data
                  ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                  .filter((item) => dayjs(item.createdAt).date() === Number(selectValue))[0]
                  .deductedPoint && (
                  <>
                    <Divider />
                    <Row justifyContent='space-between' width='100%'>
                      <Typography color='text.tertiary' fontType='body2.regular'>
                        뷰업 포인트
                      </Typography>
                      <Typography color='text.secondary' fontType='body2.medium'>
                        {(data?.data
                          ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                          .filter((item) => dayjs(item.createdAt).date() === Number(selectValue))[0]
                          .earnedPoint || 0) +
                          (data?.data
                            ?.filter((item) => dayjs(item.createdAt).month() + 1 === currentMonth)
                            .filter(
                              (item) => dayjs(item.createdAt).date() === Number(selectValue),
                            )[0].deductedPoint || 0)}
                        P
                      </Typography>
                    </Row>
                  </>
                )}
            </Column>
          </Button>
        )}
      </Column>
    </Column>
  );
};
